/**
 * Componente Web per la gestione della Wishlist tramite l'SDK ufficiale di Wishlist Hero.
 * Basato sulla documentazione: https://d2uzwr8l81w3zz.cloudfront.net/Introduction.html
 */
/**
 * Componente Wishlist con monitoraggio dello stato in tempo reale.
 */
if (!window._wlh) {
  window._wlh = {
    added: new Set(JSON.parse(localStorage.getItem("wlh_p_added") || "[]")),
    syncing: false,
  };
}

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this._isPerformingAction = false;
  }

  connectedCallback() {
    this.button = this.querySelector("button");
    if (!this.button) return;

    this.updateVariantData();
    this.initListeners();
    this.syncUI();

    if (window.WishListHero_SDK) {
      this.syncWithSDK();
    } else {
      document.addEventListener(
        "wishlist-hero-wishlist-sdk-ready",
        () => this.syncWithSDK(),
        { once: true },
      );
    }
  }

  updateVariantData() {
    this._vId = String(this.getAttribute("data-variant-id") || "").trim();
  }

  initListeners() {
    // Click handler
    this.button.removeEventListener("click", this._clickRef);
    this._clickRef = (e) => {
      e.preventDefault();
      this.toggle();
    };
    this.button.addEventListener("click", this._clickRef);

    // Cross-sync (update UI when other buttons change)
    document.addEventListener("wishlist:updated", (e) => {
      if (String(e.detail.variantId) === this._vId) {
        this.updateUI(e.detail.inWishlist);
      }
    });

    // Variant change (PDP/QuickBuy)
    document.addEventListener("variant-change", (e) => {
      const wrapper = this.closest(
        ".product-right-col, .product_card",
      );
      if (wrapper && wrapper.contains(e.target)) {
        this.setAttribute("data-variant-id", e.detail.variantId);
        this.updateVariantData();
        this.syncUI();
        this.syncWithSDK();
      }
    });
  }

  syncUI() {
    this.updateUI(window._wlh.added.has(this._vId));
  }

  async syncWithSDK() {
    if (!window.WishListHero_SDK || this._isPerformingAction) return;
    try {
      const items = window.WishListHero_SDK.GetWishListItems() || [];
      const found = items.some(
        (i) => String(i.ProductVariantId || i.variantId).trim() === this._vId,
      );

      if (found) {
        window._wlh.added.add(this._vId);
        this.saveToStorage();
      }
      this.updateUI(window._wlh.added.has(this._vId) || found);
      updateWishlistCounter();
    } catch (e) {
      console.warn("[WLH] Sync error", e);
    }
  }

  updateUI(active) {
    if (active) {
      this.button.classList.add("is-active", "text-primary");
      this.button.setAttribute("aria-pressed", "true");
    } else {
      this.button.classList.remove("is-active", "text-primary");
      this.button.setAttribute("aria-pressed", "false");
    }

    const textAdd = this.getAttribute("data-text-add");
    const textRemove = this.getAttribute("data-text-remove");
    if (textAdd && textRemove) {
      const text = active ? textRemove : textAdd;
      const textElements = this.querySelectorAll("[data-wishlist-text]");
      if (textElements.length > 0) {
        textElements.forEach((el) => {
          el.textContent = text;
        });
      }
      if (this.button.hasAttribute("title")) {
        this.button.setAttribute("title", text);
      }
    }
  }

  saveToStorage() {
    localStorage.setItem("wlh_p_added", JSON.stringify([...window._wlh.added]));
  }

  async toggle() {
    if (this._isPerformingAction || !window.WishListHero_SDK) return;
    this._isPerformingAction = true;

    // Rileggiamo l'ID corrente per sicurezza
    this.updateVariantData();

    // --- IL CUORE DELLA DECISIONE ---
    const isVisual =
      this.button.classList.contains("is-active") ||
      this.button.classList.contains("text-primary");
    const isLocal = window._wlh.added.has(this._vId);

    const shouldRemove = isVisual || isLocal;

    console.log(
      `[WLH Debug] ID: ${this._vId} | Visual: ${isVisual} | Local: ${isLocal} | Action: ${shouldRemove ? "REMOVE" : "ADD"}`,
    );

    try {
      if (typeof window.WishListHero_SDK.AddWishListItem !== "function") {
        throw new Error(
          "AddWishListItem is not a function on WishListHero_SDK",
        );
      }

      const hash = await window.WishListHero_SDK.GetWishListHash(true);
      const customerIdRaw = window.WishListHero_SDK.GetCustomerId();
      const customerId =
        customerIdRaw && customerIdRaw !== "null" && customerIdRaw !== ""
          ? parseInt(customerIdRaw)
          : 0;

      if (shouldRemove) {
        // AZIONE: RIMOZIONE
        console.log("[WLH Debug] Attempting REMOVE for:", this._vId);
        const res = await window.WishListHero_SDK.DeleteWishListItem({
          ProductVariantId: parseInt(this._vId),
          WishlistHash: hash,
        });
        console.log("[WLH Debug] Delete Response:", res);
        window._wlh.added.delete(this._vId);
      } else {
        // AZIONE: AGGIUNTA
        const productInfo = {
          CustomerId: customerId,
          ProductId: parseInt(this.getAttribute("data-product-id")),
          ProductTitle: this.getAttribute("data-name"),
          ProductPrice: parseFloat(
            String(this.getAttribute("data-price") || "0")
              .replace(",", ".")
              .replace(/[^0-9.]/g, ""),
          ),
          ProductLink:
            window.location.origin +
            (this.getAttribute("data-product-url") ||
              "/products/" +
                (this.getAttribute("data-product-handle") || "")
                  .split("/")
                  .pop()),
          ProductVariantId: parseInt(this._vId),
          ProductImage: this.getAttribute("data-image"),
          WishlistHash: hash,
        };

        console.log(
          "[WLH Debug] Attempting ADD with Info:",
          JSON.stringify(productInfo, null, 2),
        );
        const res = await window.WishListHero_SDK.AddWishListItem(productInfo);
        console.log("[WLH Debug] Add Response:", res);

        if (res === true || (res && typeof res === "object")) {
          window._wlh.added.add(this._vId);
        } else {
          console.error("[WLH] AddWishListItem result was not success:", res);
        }
      }

      this.saveToStorage();
      const finalState = window._wlh.added.has(this._vId);
      this.updateUI(finalState);

      // Notifica tutti i bottoni dello stesso prodotto
      document.dispatchEvent(
        new CustomEvent("wishlist:updated", {
          detail: { variantId: this._vId, inWishlist: finalState },
        }),
      );
    } catch (err) {
      console.error("[WLH] Toggle failed with error:", err);
    } finally {
      setTimeout(() => {
        this._isPerformingAction = false;
      }, 500);
    }
  }
}

if (!customElements.get("wishlist-button")) {
  customElements.define("wishlist-button", WishlistButton);
}

/**
 * Aggiorna il testo di tutti i contatori della wishlist trovati nella pagina.
 */
export function updateWishlistCounter() {
  const counterElements = document.querySelectorAll("[data-wishlist-count]");
  if (counterElements.length === 0) return;

  let count = 0;
  if (window.WishListHero_SDK) {
    const items = window.WishListHero_SDK.GetWishListItems() || [];
    count = items.length;
  } else if (window._wlh && window._wlh.added) {
    count = window._wlh.added.size;
  }

  counterElements.forEach((el) => {
    el.textContent = count || ''; // svuota se 0 per :empty CSS
  });
}

// Aggiorna immediatamente all'avvio / caricamento del DOM
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", updateWishlistCounter);
} else {
  updateWishlistCounter();
}

// Aggiorna quando l'SDK è pronto
document.addEventListener("wishlist-hero-wishlist-sdk-ready", updateWishlistCounter);

// Aggiorna quando un prodotto viene aggiunto o rimosso
document.addEventListener("wishlist:updated", updateWishlistCounter);